import { Box, Typography } from "@mui/material";
import { useTranslation } from "react-i18next";
import { common, financedEmissions } from "../../i18n";
import { IntensityScale } from "../../models/dto/cat15/cat15.types";
import InfoPopper from "../popper/InfoPopper";

interface Props {
  scale: IntensityScale;
}

// Tier colors mirror DESIGN.md status tokens (low=green, medium=amber, high=red).
const DOT = {
  low: "var(--green)",
  medium: "var(--yellow)",
  high: "var(--red)",
} as const;

/** Intensity tier legend (Low / Medium / High chips) shown under intensity charts. */
const IntensityLegend = ({ scale }: Props) => {
  const { t } = useTranslation([financedEmissions, common], {
    nsMode: "fallback",
  });

  const chips: { key: keyof typeof DOT; label: string }[] = [
    { key: "low", label: t("Intensity-Low", { value: scale.low }) },
    {
      key: "medium",
      label: t("Intensity-Medium", { low: scale.low, high: scale.high }),
    },
    { key: "high", label: t("Intensity-High", { value: scale.high }) },
  ];

  return (
    <Box
      display="flex"
      alignItems="center"
      flexWrap="wrap"
      gap="10px"
      mt="12px"
      pt="12px"
      sx={{ borderTop: "1px solid var(--gray6)" }}
    >
      <Box display="flex" alignItems="center" gap="4px">
        <Typography variant="body2" color="var(--gray1)">
          {t("Intensity-Scale-Label")}
        </Typography>
        <InfoPopper text={t("Intensity-Scale-Info")} />
      </Box>
      {chips.map((c) => (
        <Box key={c.key} display="flex" alignItems="center" gap="6px">
          <Box
            width="10px"
            height="10px"
            borderRadius="50%"
            bgcolor={DOT[c.key]}
          />
          <Typography variant="body2" color="var(--gray9)">
            {c.label}
          </Typography>
        </Box>
      ))}
    </Box>
  );
};

export default IntensityLegend;
